import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFinanceApi, adminFinanceKeys } from '@/features/admin-finance/api/admin-finance.api';
import {
  financeListSx,
  financeRowSx,
  financeToolbarSx,
} from '@/features/admin-finance/styles/financeSurfaces';

export function FinanceCommercialPanel() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [guideId, setGuideId] = useState('');
  const [commissionType, setCommissionType] = useState('PERCENTAGE');
  const [commissionValue, setCommissionValue] = useState('10');
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isLoading, error } = useQuery({
    queryKey: adminFinanceKeys.commercial({}),
    queryFn: () => adminFinanceApi.listCommercial({ limit: 50 }),
  });

  const create = useMutation({
    mutationFn: () =>
      adminFinanceApi.createCommercial({
        guideId: guideId.trim(),
        commissionType,
        commissionValue: Number(commissionValue),
        effectiveFrom: new Date(effectiveFrom).toISOString(),
      }),
    onSuccess: () => {
      setShowForm(false);
      setGuideId('');
      void qc.invalidateQueries({ queryKey: adminFinanceKeys.commercial() });
    },
  });

  const agreements = data?.data ?? [];

  return (
    <Box>
      <Box sx={{ ...financeListSx, mb: showForm ? 2 : 0 }}>
        <Box sx={financeToolbarSx}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            Commission agreements
          </Typography>
          <Button
            size="small"
            variant={showForm ? 'outlined' : 'contained'}
            sx={{ borderRadius: 0, textTransform: 'none' }}
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? 'Close' : 'Set commission'}
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 0, m: 2 }}>
            Failed to load commercial agreements.
          </Alert>
        )}
        {!isLoading && agreements.length === 0 && (
          <Alert severity="info" sx={{ borderRadius: 0, m: 2 }}>
            No custom agreements. Platform default applies.
          </Alert>
        )}

        {agreements.map((a) => (
          <Box key={a.guideId} sx={financeRowSx}>
            <Typography fontWeight={700}>Guide {a.guideId.slice(-8)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {a.commissionType === 'PERCENTAGE'
                ? `${a.commissionValue}% commission`
                : `Fixed ${a.commissionValue}`}{' '}
              · {a.status}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              From {a.effectiveFrom?.slice(0, 10) ?? '—'}
              {a.effectiveTo ? ` to ${a.effectiveTo.slice(0, 10)}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>

      {showForm && (
        <Box
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            bgcolor: 'background.paper',
          }}
        >
          <TextField
            fullWidth
            label="Guide user ID"
            value={guideId}
            onChange={(e) => setGuideId(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
          <TextField
            select
            fullWidth
            label="Commission type"
            value={commissionType}
            onChange={(e) => setCommissionType(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          >
            <MenuItem value="PERCENTAGE">Percentage</MenuItem>
            <MenuItem value="FIXED">Fixed</MenuItem>
          </TextField>
          <TextField
            fullWidth
            type="number"
            label={commissionType === 'PERCENTAGE' ? 'Percent' : 'Fixed amount'}
            value={commissionValue}
            onChange={(e) => setCommissionValue(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
          <TextField
            fullWidth
            type="date"
            label="Effective from"
            InputLabelProps={{ shrink: true }}
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
          <Button
            variant="contained"
            sx={{ borderRadius: 0 }}
            disabled={!guideId.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Save agreement
          </Button>
          {create.isError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 0 }}>
              Failed to save commercial agreement.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
